export const CONDITION_INTERVAL_MS = 50

function doWhen(condition: () => boolean, fn: () => void, attempt = 0) {
  const shouldDo = condition()
  if (!shouldDo) {
    if (attempt > 10) {
      console.error('doWhen condition never met')
      return
    }
    // Try again
    setTimeout(() => doWhen(condition, fn, attempt + 1), CONDITION_INTERVAL_MS)
    return
  }

  fn()
}

// Map of elements to their last known top position
const lastElementTop = new WeakMap<HTMLElement, number | undefined>()

function didElementMove(target: HTMLElement) {
  const currentTop = target.getBoundingClientRect().top + window.scrollY

  const didMove = currentTop !== lastElementTop.get(target)
  lastElementTop.set(target, currentTop)

  return didMove
}

// Scrolls the element into view when stops moving
// Waits up until 1 s checks every 100 ms
// NOTE: this is based on a getBoundingClientRect().top which DOES NOT take height clipping into account (like Collapsible)
export function scrollIntoViewWhenStops(targetGetter: () => HTMLElement | null) {
  let initialized = false
  doWhen(
    // Wait for the element to stop moving
    () => {
      const target = targetGetter()
      if (!target) return false

      // Reset lastElementTop for this element
      if (!initialized) {
        initialized = true
        lastElementTop.delete(target)
      }

      return !didElementMove(target)
    },
    // Scroll the element into view
    () => {
      const target = targetGetter()
      if (!target) return

      // scrollIntoView() scrolls all ancestor containers, so we need to manually scroll only the nearest one

      const container = target.closest('.overflow-y-auto') as HTMLElement
      if (!container) return

      const targetRect = target.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()

      // replicate block: 'nearest' behavior — don't scroll if fully visible, otherwise scroll minimum amount
      const targetTop = targetRect.top - containerRect.top
      const targetBottom = targetRect.bottom - containerRect.top
      const containerHeight = container.clientHeight

      let scrollDelta = 0
      if (targetTop < 0) {
        // Element is above the visible area — align its top with the container top
        scrollDelta = targetTop
      } else if (targetBottom > containerHeight) {
        // Element is below the visible area — align its bottom with the container bottom
        scrollDelta = targetBottom - containerHeight
      }
      // else: fully visible, no scroll needed

      if (scrollDelta !== 0) {
        container.scrollTo({
          top: container.scrollTop + scrollDelta,
          behavior: 'smooth',
        })
      }
    },
  )
}
